import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { adminApi } from "../api";
import { Loader2, Search, Users, Calendar, ChevronLeft, Trophy, UserPlus, Sparkles, ArrowUpRight } from "lucide-react";

const QUARTERS = [
  { v: null, label: "Todos" },
  { v: 1, label: "1.º Trim." },
  { v: 2, label: "2.º Trim." },
  { v: 3, label: "3.º Trim." },
  { v: 4, label: "4.º Trim." },
];

const HEAD_CLS = "text-[10px] uppercase tracking-[0.15em] text-[#978D78] font-bold px-3 py-2";

const EUR = (v) =>
  v == null ? "—" : new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(Number(v));

const AWARDS = [
  { key: "best_seller",   label: "Best Seller",   subtitle: "Consumo + vendas directas",  Icon: Trophy,    accent: "text-amber-700",   bg: "bg-amber-50",    border: "border-amber-200",   fmt: (v) => EUR(v) },
  { key: "best_sponsor",  label: "Best Sponsor",  subtitle: "Mais novos consultores", Icon: UserPlus, accent: "text-sky-700",     bg: "bg-sky-50",      border: "border-sky-200",     fmt: (v) => `${v ?? 0} novos` },
  { key: "best_promoter", label: "Best Promoter", subtitle: "Mais clientes finais",  Icon: Sparkles, accent: "text-emerald-700", bg: "bg-emerald-50",  border: "border-emerald-200", fmt: (v) => `${v ?? 0} clientes` },
];

function AwardCard({ award, winner }) {
  if (!winner) {
    return (
      <div className={`p-3 rounded border ${award.border} ${award.bg} opacity-60`}>
        <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] font-bold ${award.accent}`}>
          <award.Icon size={12} /> {award.label}
        </div>
        <div className="mt-2 text-xs text-[#6B7280] italic">Sem dados</div>
      </div>
    );
  }
  return (
    <div className={`p-3 rounded border ${award.border} ${award.bg}`}
      data-testid={`award-${award.key}`}>
      <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] font-bold ${award.accent}`}>
        <award.Icon size={12} /> {award.label}
      </div>
      <div className="mt-2">
        <Link to={`/admin/t/cliente/Tinf_Cliente/view/${winner.ID_Cliente}`}
          className="text-sm font-bold text-[#0F1829] hover:underline"
          data-testid={`award-${award.key}-name`}>
          {winner.NomeCompleto || `#${winner.ID_Cliente}`}
        </Link>
        <div className="text-[11px] text-[#6B7280] truncate">{winner.Email || ""}</div>
        <div className={`mt-1 font-mono font-bold ${award.accent}`}
          data-testid={`award-${award.key}-value`}>{award.fmt(winner.value)}</div>
      </div>
      <div className="text-[10px] text-[#6B7280] mt-1">{award.subtitle}</div>
    </div>
  );
}

function PinUpgradesList({ upgrades }) {
  if (!upgrades || upgrades.length === 0) {
    return (
      <div className="px-3 py-2 border-t border-[#F3F0E7] text-[11px] text-[#6B7280] italic">
        Sem subidas de pin neste trimestre.
      </div>
    );
  }
  return (
    <div className="border-t border-[#F3F0E7]" data-testid="pin-upgrades">
      <div className="px-3 pt-2 pb-1 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] font-bold text-violet-700">
        <ArrowUpRight size={12} /> Subiram de Pin ({upgrades.length})
      </div>
      <ul className="px-3 pb-2 divide-y divide-[#F3F0E7]">
        {upgrades.map((u) => (
          <li key={u.ID_Cliente} data-testid={`pin-upgrade-${u.ID_Cliente}`}
            className="py-1 text-[12px] flex items-center justify-between gap-2">
            <Link to={`/admin/t/cliente/Tinf_Cliente/view/${u.ID_Cliente}`}
              className="font-medium text-[#0F1829] hover:underline truncate">
              {u.NomeCompleto || `#${u.ID_Cliente}`}
            </Link>
            <span className="font-mono text-[11px] text-[#4B5563] whitespace-nowrap">
              Pin <span className="text-[#978D78]">{u.PinAntigo ?? 0}</span>
              {" → "}<span className="font-bold text-violet-700">{u.PinNovo}</span>
              {u.NivelNovo ? (
                <>
                  {" "}<span className="opacity-50">·</span>{" "}
                  <span className="text-[#978D78]">{u.NivelAntigo || "—"}</span>
                  {" → "}<span className="font-bold text-violet-700">{u.NivelNovo}</span>
                </>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function QuarterAwards({ quarter, winners }) {
  return (
    <div className="border border-[#E6E1D6] rounded-lg overflow-hidden" data-testid={`awards-q${quarter}`}>
      <div className="bg-[#0F1829] text-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.15em]">
        {quarter}.º Trimestre
      </div>
      <div className="p-2 grid grid-cols-1 sm:grid-cols-3 gap-2 bg-white">
        {AWARDS.map((a) => <AwardCard key={a.key} award={a} winner={winners?.[a.key]} />)}
      </div>
      <PinUpgradesList upgrades={winners?.pin_upgrades} />
    </div>
  );
}

export default function AdminConsultantRenewals() {
  const [params, setParams] = useSearchParams();
  const quarterParam = parseInt(params.get("quarter") || "", 10) || null;
  const qParam = params.get("q") || "";

  // Year is always the current year — não é configurável por design.
  const year = new Date().getFullYear();
  const [quarter, setQuarter] = useState(quarterParam);
  const [q, setQ] = useState(qParam);

  const [data, setData] = useState({ data: [], totals_by_quarter: {}, total: 0, year: null, quarter: null });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [awards, setAwards] = useState(null);
  const [awardsLoading, setAwardsLoading] = useState(true);

  // Sync URL → state on first mount and when external nav changes.
  useEffect(() => {
    setQuarter(parseInt(params.get("quarter") || "", 10) || null);
    setQ(params.get("q") || "");
  }, [params]);

  // Reload on any filter change. Year is fixed to current year.
  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr("");
    adminApi.consultantRenewals({ year, quarter, q: q.trim() })
      .then((r) => { if (!cancelled) setData(r || {}); })
      .catch((e) => { if (!cancelled) setErr(e.message || "Erro"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [year, quarter, q]);

  // Awards depend only on the year — fetch once per mount.
  useEffect(() => {
    let cancelled = false;
    setAwardsLoading(true);
    adminApi.quarterlyAwards(year)
      .then((r) => { if (!cancelled) setAwards(r); })
      .catch(() => { if (!cancelled) setAwards(null); })
      .finally(() => { if (!cancelled) setAwardsLoading(false); });
    return () => { cancelled = true; };
  }, [year]);

  const totals = data.totals_by_quarter || {};
  const effectiveYear = data.year || year;

  const update = (next) => {
    const sp = new URLSearchParams(params);
    Object.entries(next).forEach(([k, v]) => {
      if (v == null || v === "") sp.delete(k);
      else sp.set(k, String(v));
    });
    setParams(sp);
  };

  return (
    <div className="max-w-6xl mx-auto p-4" data-testid="admin-consultant-renewals">
      <div className="flex items-center gap-2 mb-4">
        <Link to="/admin" className="text-xs text-[#6B7280] hover:text-[#0F1829] inline-flex items-center gap-1">
          <ChevronLeft size={12} /> Dashboard
        </Link>
      </div>
      <header className="flex flex-wrap items-end gap-4 justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-[#0F1829] flex items-center gap-2" style={{ fontFamily: "'Fraunces', serif" }}>
            <Users size={22} /> Renovações de Consultores
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">
            Consultores activos cuja <strong>data de validade</strong> cai em <strong>{effectiveYear}</strong> (ano corrente), com anos completos como consultor calculados até ao fim do trimestre.
          </p>
        </div>
      </header>

      <div className="bg-white border border-[#E6E1D6] rounded-lg p-3 mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-[#978D78] font-bold block">Trimestre</label>
          <div className="mt-1 flex gap-1">
            {QUARTERS.map((qt) => (
              <button
                key={String(qt.v)}
                onClick={() => update({ quarter: qt.v })}
                data-testid={`renewals-quarter-${qt.v ?? 'all'}`}
                className={`px-3 py-1.5 text-xs rounded border transition ${
                  (quarter || null) === qt.v
                    ? "bg-[#0F1829] text-white border-[#0F1829]"
                    : "bg-white border-[#E6E1D6] hover:bg-[#FAF8F2]"
                }`}
              >
                {qt.label}
                {qt.v != null && totals[qt.v] ? <span className="ml-1 text-[10px] opacity-70">({totals[qt.v]})</span> : null}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="text-[10px] uppercase tracking-[0.15em] text-[#978D78] font-bold block">Pesquisar</label>
          <div className="mt-1 relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#978D78]" />
            <input
              type="search"
              value={q}
              onChange={(e) => update({ q: e.target.value })}
              data-testid="renewals-search"
              placeholder="Nome ou email"
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-[#E6E1D6] rounded focus:border-[#0F1829] outline-none"
            />
          </div>
        </div>
      </div>

      {err && (
        <div className="px-3 py-2 mb-3 bg-red-50 border border-red-200 rounded text-sm text-red-800" data-testid="renewals-error">{err}</div>
      )}

      {/* Quarterly awards — 3 prémios por trimestre */}
      <section className="mb-5" data-testid="quarterly-awards">
        <div className="flex items-center gap-2 mb-2">
          <Trophy size={14} className="text-amber-700" />
          <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-[#0F1829]">Prémios Trimestrais {effectiveYear}</h2>
          {awardsLoading && <Loader2 size={12} className="animate-spin text-[#6B7280]" />}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {[1, 2, 3, 4]
            .filter((qt) => !quarter || quarter === qt)
            .map((qt) => (
              <QuarterAwards key={qt} quarter={qt} winners={awards?.quarters?.[qt]} />
            ))}
        </div>
      </section>

      <div className="bg-white border border-[#E6E1D6] rounded-lg overflow-hidden">
        <div className="px-3 py-2 border-b border-[#F3F0E7] flex items-center justify-between text-xs text-[#6B7280]">
          <div>
            <span className="font-medium text-[#0F1829]" data-testid="renewals-total">{data.total || 0}</span> renovações em {effectiveYear}{quarter ? ` (T${quarter})` : ""}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF8F2] border-b border-[#E6E1D6]">
              <tr>
                <th className={`${HEAD_CLS} text-left`}>ID</th>
                <th className={`${HEAD_CLS} text-left`}>Consultor</th>
                <th className={`${HEAD_CLS} text-left`}>Email</th>
                <th className={`${HEAD_CLS} text-left`}>Início como Consultor</th>
                <th className={`${HEAD_CLS} text-left`}>Data Validade</th>
                <th className={`${HEAD_CLS} text-left`}>Data Renovação</th>
                <th className={`${HEAD_CLS} text-center`}>Trim.</th>
                <th className={`${HEAD_CLS} text-right`}>Anos</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="text-center py-8 text-[#6B7280]"><Loader2 size={14} className="inline animate-spin mr-1" /> A carregar...</td></tr>
              )}
              {!loading && (data.data || []).length === 0 && (
                <tr><td colSpan={8} className="text-center py-8 text-[#6B7280]">Sem renovações registadas para este filtro.</td></tr>
              )}
              {!loading && (data.data || []).map((r) => (
                <tr key={r.ID_Cliente} data-testid={`renewal-row-${r.ID_Cliente}`} className={`border-b border-[#F3F0E7] hover:bg-[#FAF8F2] ${r.JaRenovada ? 'bg-emerald-50/40' : ''}`}>
                  <td className="px-3 py-2 font-mono text-[#0F1829]">
                    <Link to={`/admin/t/cliente/Tinf_Cliente/view/${r.ID_Cliente}`} className="hover:underline">#{r.ID_Cliente}</Link>
                  </td>
                  <td className="px-3 py-2 font-medium" data-testid={`renewal-name-${r.ID_Cliente}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span>{r.NomeCompleto || "—"}</span>
                      {r.JaRenovada ? (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-emerald-100 text-emerald-800 border border-emerald-200"
                          data-testid={`renewal-badge-${r.ID_Cliente}`}
                          title={`Renovada em ${r.DataRenovacao}. Próxima validade: ${r.DataValidade}.`}
                        >
                          ✓ Renovada
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-[#4B5563]">{r.Email || "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs text-[#4B5563]">{r.DataInicio || "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs text-[#0F1829] font-semibold" data-testid={`renewal-validade-${r.ID_Cliente}`}>
                    <span className="inline-flex items-center gap-1"><Calendar size={11} /> {r.DataValidade || "—"}</span>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-[#4B5563]">{r.DataRenovacao || "—"}</td>
                  <td className="px-3 py-2 text-center text-[#4B5563]">{r.Trimestre || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono font-semibold text-[#0F1829]" data-testid={`renewal-years-${r.ID_Cliente}`}>{r.AnosConsultor ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
